import { useEffect, useState } from "react";
import api from "../lib/api";

const STORAGE_KEY = "ak_current_location_v1";
const TTL_MS = 6 * 60 * 60 * 1000; // 6h — long enough not to re-prompt across a session, short enough to track real moves

function readCache() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.ts || Date.now() - parsed.ts > TTL_MS) return null;
    return parsed.value;
  } catch {
    return null;
  }
}

function writeCache(value) {
  try {
    const payload = JSON.stringify({ ts: Date.now(), value });
    sessionStorage.setItem(STORAGE_KEY, payload);
    localStorage.setItem(STORAGE_KEY, payload);
  } catch { /* noop */ }
}

/**
 * Returns the seeker's CURRENT location (not birth coords) so Today's Brief
 * and the Panchang bar can show the panchang for where they actually are.
 *
 * Strategy:
 *  1. Use the cached value if it's fresh (within 6 hours).
 *  2. Otherwise ask the browser Geolocation API once.
 *  3. Reverse-geocode the coords via /api/geo/reverse to get a city label
 *     and IANA timezone.
 *  4. Cache and return.
 *
 * If geolocation is denied or unavailable, returns `{ status: 'unavailable' }`
 * so callers can fall back to their old behaviour.
 */
export default function useCurrentLocation({ enabled = true } = {}) {
  const [state, setState] = useState(() => {
    const cached = readCache();
    return cached
      ? { status: "ready", ...cached }
      : { status: "idle" };
  });

  useEffect(() => {
    if (!enabled) return;
    if (state.status !== "idle") return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setState({ status: "unavailable", reason: "no-api" });
      return;
    }
    setState({ status: "loading" });
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const { data } = await api.get("/geo/reverse", {
            params: { lat: latitude, lon: longitude },
          });
          const value = {
            lat: data.lat,
            lon: data.lon,
            city: data.city || "your area",
            timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          };
          writeCache(value);
          setState({ status: "ready", ...value });
        } catch {
          // Reverse-geocode failed — still use the raw coords with the
          // browser-detected timezone so the panchang at least lands locally.
          const value = {
            lat: Number(latitude.toFixed(4)),
            lon: Number(longitude.toFixed(4)),
            city: "your area",
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };
          writeCache(value);
          setState({ status: "ready", ...value });
        }
      },
      (err) => {
        setState({ status: "unavailable", reason: err?.code === 1 ? "denied" : "error" });
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  }, [enabled, state.status]);

  return state;
}
